import { create } from 'zustand'
import { devtools } from 'utils/devtools'
import { useTranslation } from 'react-i18next'
import { Stage, preset, Preset, presets } from '@shell/Topbar/types'

export type Back = (() => void) | boolean

type StagesState = {
  preset: Preset
  list: Stage[]
  active: Stage | null
  back: Back
  isLogo: boolean
  isModal: boolean
  dropdown: string | Preset
}

type StagesActions = {
  setStage: (stage: Stage, preset?: Preset, dropdown?: string | Preset) => void
  setBack: (back: Back) => void
  setIsLogo: (isLogo: boolean) => void
  setIsModal: (isModal: boolean) => void
  reset: () => void
}

const PresetArray = Object.keys(presets) as Preset[]

const defaults = {
  preset: preset.empty,
  list: presets.empty,
  active: null,
  back: false,
  isLogo: false,
  isModal: false,
  dropdown: preset.empty,
} satisfies StagesState

export const useStagesStore = create<StagesState & StagesActions>()(
  devtools(
    (set) => ({
      ...defaults,
      setStage: (stage, preset, dropdown) =>
        set((state) => {
          // Find which preset contains this stage

          const isPresetOverride =
            preset && preset !== state.preset && presets[preset].includes(stage)

          const isPresetCurrent =
            preset === state.preset && stage && state.list.includes(stage)

          const findPreset = PresetArray.find((key) =>
            presets[key].includes(stage)
          )

          const newPreset = isPresetOverride
            ? preset
            : isPresetCurrent
            ? state.preset
            : findPreset

          // If preset found, update preset, list, and active
          if (newPreset) {
            const newList = presets[newPreset]

            const newActive = newList.find((item) => item === stage)

            return {
              preset: newPreset,
              list: newList,
              dropdown: dropdown ?? newPreset,
              active: newActive,
            }
          }

          // If stage not found in any preset, return unchanged
          return state
        }),
      setBack: (back) => set({ back }),
      setIsLogo: (isLogo) => set({ isLogo }),
      setIsModal: (isModal) => set({ isModal }),
      reset: () => set(defaults),
    }),
    { name: 'stages' }
  )
)

export const useStages = () => {
  const { t } = useTranslation(['ui'])
  const { preset, list, active } = useStagesStore((state) => state)

  const getStageIndex = (stage?: Stage) => {
    const index = list.findIndex((item) => item === (stage || active)) || 0
    return index
  }

  const getLables = (preset?: Preset) => {
    const stages = preset ? presets[preset] : list
    const labels = stages.map((stage) => (stage ? `ui:stage.${stage}` : null))
    return labels
  }

  return { getStageIndex, getLables }
}
